export enum StepState {
	COMPLETED = 'completed',
	ACTIVE = 'active',
	PENDING = 'pending',
	ERROR = 'error'
}

export enum B2cFlowType {
	NEW_PLAN = 'new_plan',
	TRANSFER_CONFIRMED = 'transfer_confirmed'
}

/**
 * 🔴 Aquí hubo un tercero, `PLAN_DETECTIVE`: un traspaso cuyo plan de origen no se llegó a
 * identificar. Se fue el 30/09/26 por dos motivos que se sostienen el uno al otro:
 *
 *  1. Ya no se puede entrar. El funnel exige elegir plan, así que nadie nuevo cae ahí
 *     (Marisa, 30/09/26). En PROD queda UNA petición de las de antes —`payflow`, `requested`,
 *     sin plan bloqueado, abierta desde el 15/04/26—, medido el 30/09/26.
 *  2. No hacía nada. `ExplorationView` era el único sitio que lo miraba, para elegir saludo y
 *     subtítulo entre tres claves… cuyos textos eran IDÉNTICOS palabra por palabra, en español
 *     y en inglés. Un ternario entre cadenas iguales.
 *
 * Esa única petición cae ahora en `PLAN_LOCATED` y ve exactamente lo mismo que veía. El nombre
 * le queda grande —su plan de origen sigue sin identificar—, pero la distinción que sí tiene
 * efecto es `NO_PLAN` (alta) contra el resto (traspaso), y esa se conserva entera.
 */
export enum B2cLandingScenario {
	NO_PLAN = 'no-plan',
	PLAN_LOCATED = 'plan-located'
}

/* `catalogId` sigue en la firma aunque esta función ya no lo mire: era lo único que decidía
   `PLAN_DETECTIVE`, y los tests lo pasan a propósito para fijar que el catálogo YA NO cambia el
   resultado. Sacarlo del tipo haría que esos casos no compilaran y se perdería esa comprobación. */
export function detectLandingScenario(transfer: {
	metadata?: Record<string, any>;
	catalogId?: string | null;
}): B2cLandingScenario {
	const tipo = transfer.metadata?.tipo;
	if (tipo === B2cFlowType.NEW_PLAN) {
		return B2cLandingScenario.NO_PLAN;
	}
	return B2cLandingScenario.PLAN_LOCATED;
}

// Backend sentinel used when a transfer has no real origin plan.
export const DGSFP_NO_CODE = 'SIN-CODIGO';

/**
 * Un código DGSFP de verdad, con la MISMA regla que comparsa (`utils/is-real-dgsfp.ts`): no
 * vacío y distinto del centinela, sin distinguir mayúsculas.
 *
 * Existe para que la plataforma no ofrezca firmar lo que el backend va a rechazar: sin plan de
 * origen real, el boletín saldría con el origen en blanco y la llamada devuelve «Transfer
 * request has no real origin plan».
 */
export const esDgsfpReal = (code?: string | null): boolean => {
	const valor = (code ?? '').trim();
	return valor.length > 0 && valor.toUpperCase() !== DGSFP_NO_CODE;
};

/**
 * Returns true when the transfer represents a new-plan opening (no origin plan),
 * as opposed to a real traspaso with an existing plan as source.
 *
 * Primary signal: metadata.tipo === 'new_plan' (set by the onboarding funnel).
 * Fallback: DGSFP_code is absent or the backend sentinel, with no origin in payload.
 */
export function isNewPlanTransfer(transfer: {
	metadata?: Record<string, any>;
	catalogId?: string | null;
	DGSFP_code?: string;
	originPayload?: Record<string, any>;
}): boolean {
	if (detectLandingScenario(transfer) === B2cLandingScenario.NO_PLAN) return true;
	return (
		(!transfer.DGSFP_code || transfer.DGSFP_code === DGSFP_NO_CODE) &&
		!transfer.originPayload?.dgsfpCode
	);
}
